import {
  __,
  both,
  complement,
  curry,
  either,
  flatten,
  gt,
  head,
  isEmpty,
  isNil,
  last,
  length,
  lensProp,
  omit,
  path,
  pipe,
  prop,
  set,
  sum,
  reject,
  unless,
  values,
} from 'ramda';
import { memoize } from 'lodash';
import map from 'lodash/fp/map';
import filter from 'lodash/fp/filter';
import find from 'lodash/fp/find';
import { createSelector } from 'reselect';

import { ITEM_TYPE } from '../constants';
import { isHashMapEmpty } from '../../common/helpers/functional';

export const getOrderItems = path(['register', 'order', 'barberItemsById']);

const flattenBarberItems = pipe(values, flatten);
const getBarberItemsArray = (state, id) => pipe(
  getOrderItems,
  prop(id),
  flattenBarberItems,
)(state);

export const getOrderDiscounts = path(['register', 'order', 'discounts']);

const mapId = map('id');
const getSelectedItems = memoize(type => pipe(
  getBarberItemsArray,
  filter(['type', type]),
  mapId,
));

export const getSelectedProducts = getSelectedItems(ITEM_TYPE.product);

export const getSelectedServices = getSelectedItems(ITEM_TYPE.service);

const getSelectedAppointments = getSelectedItems(ITEM_TYPE.appointment);

export const hasProduct = pipe(getSelectedProducts, complement(isEmpty));
const hasService = pipe(getSelectedServices, complement(isEmpty));
const hasAppointment = pipe(getSelectedAppointments, complement(isEmpty));
export const hasServiceOrAppointment = either(hasService, hasAppointment);

export const getSelectedDiscounts = pipe(getOrderDiscounts, mapId);

export const getSelectedMembership = path(['register', 'order', 'membership']);

export const getBarbers = path(['register', 'order', 'barbers']);

export const getNoBarbers = pipe(getBarbers, isEmpty);

export const getFirstBarber = pipe(getBarbers, head);

export const getBarberItems =
  (state, barberId) => path(['register', 'order', 'barberItems', barberId], state);

export const getBarberItem =
  (state, barberId, id) => path(['register', 'order', 'barberItemsById', barberId, id], state);

export const getIsOrderBarberItemsEmpty = pipe(
  path(['register', 'order', 'barberItems']),
  isHashMapEmpty,
);

export const getIsOrderMembershipEmpty = pipe(
  getSelectedMembership,
  isNil,
);

export const getIsOrderEmpty = both(
  getIsOrderBarberItemsEmpty,
  getIsOrderMembershipEmpty
);

export const getHasManyBarbers = pipe(
  getBarbers,
  length,
  gt(__, 1)
);

const isBarberSelected = curry((state, barber) => getBarbers(state).includes(barber.id));

export const getNotSelectedBarbers = (state, allBarbers) => reject(isBarberSelected(state), allBarbers);

export const getIsOnlyOneBarberSelected = both(
  isBarberSelected,
  complement(getHasManyBarbers)
);

const flattenAllItems = pipe(values, flatten, map(values), flatten);

const getMembereshipOrderItem = pipe(
  getSelectedMembership,
  unless(isNil, set(lensProp('type'), 'membership'))
);

export const getAssignableOrderItems = pipe(getOrderItems, flattenAllItems);

export const getAllOrderItems = createSelector(
  getAssignableOrderItems,
  getMembereshipOrderItem,
  (assignedItems, membershipItem) => {
    if (membershipItem) {
      return [...assignedItems, membershipItem];
    }
    return assignedItems;
  }
);

const getOrderAppointments = pipe(
  getAssignableOrderItems,
  filter({ type: ITEM_TYPE.appointment }),
);

export const getOrderHasAppointment = pipe(
  getAssignableOrderItems,
  find({ type: ITEM_TYPE.appointment }),
  Boolean,
);

export const getSelectedGiftCards = pipe(
  getAssignableOrderItems,
  filter({ type: ITEM_TYPE.giftCard }),
);

export const getCustomer = pipe(
  getOrderAppointments,
  map(prop('customer')),
  filter(customer => !customer.deleted),
  last,
);

export const getAvailableInventory = (state, barberId, item) => {
  if (!isFinite(item.inventory)) {
    return null;
  }

  // count how many items are ordered in all other barbers sub-orders except barberId
  const subOrdersExceptCurrent = pipe(getOrderItems, omit([barberId]))(state);
  const flatOrderItemsArray = pipe(values, map(values), flatten)(subOrdersExceptCurrent);
  const selectedOrderItems = filter(orderItem => orderItem.id === item.id)(flatOrderItemsArray);
  const selectedItemOrderedQuantity = pipe(map(prop('quantity')), sum)(selectedOrderItems);

  return item.inventory - selectedItemOrderedQuantity;
};


export const getIsPreserveOrderCustomer = path(['register', 'order', 'preserveOrderCustomer']);
